import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCurrentUser, useWorkspace } from 'components/providers'
import { pick } from 'lodash-es'
import { useContext } from 'react'
import { useParams } from 'react-router'
import { DBProps, IEvent, QRCodeConfiguration, dbProps } from 'types'
import { createTimestamp, getDocs, getFileUrl, useWorkspaces } from 'utils'
import defaultEventImage from '../assets/event_tile.png'
import { EventContext } from '../components/event'

export type EventRecord = PartialEvent & ExtraEventRecordProps

type PartialEvent = Pick<IEvent, keyof DBProps | (typeof partialEventFields)[number]>

interface ExtraEventRecordProps {
  status: EventStatus
  imageUrl?: string
}

const partialEventFields = [
  'endTime',
  'hasImage',
  'leadCount',
  'location',
  'managerId',
  'memberIds',
  'sessionCount',
  'startTime',
  'title',
  'workspaceId',
] as const satisfies string[]

export type EventStatus = 'live' | 'upcoming' | 'past'

const makeQueryKey = (workspaceIds?: string[]) => ['events', workspaceIds]

export function useEvents() {
  const workspaceId = useParams().workspaceId
  const { workspaces } = useWorkspaces('emp', true)
  const workspaceIds =
    workspaceId === 'all' ? workspaces?.map(w => w.id) : workspaceId ? [workspaceId] : []
  const { data, ...rest } = useQuery({
    queryKey: makeQueryKey(workspaceIds),
    queryFn: () => getEvents(workspaceIds!),
    staleTime: 1000 * 30,
    enabled: !!workspaceIds?.length,
  })

  return { events: data, ...rest }
}

export function useMutateEventRecords() {
  const user = useCurrentUser()
  const { workspace } = useWorkspace()
  const queryClient = useQueryClient()

  async function updateEventRecord(body: IEvent) {
    if (!user || !workspace) return

    const record = await makeEventRecord(body)

    queryClient.setQueriesData<EventRecord[]>(
      { queryKey: makeQueryKey([workspace.id]) },
      events => {
        if (!events) return [record]

        const eventIndex = events.findIndex(e => e.id === record.id)
        if (eventIndex < 0) return sortEventRecords([...events, record])

        return sortEventRecords(events.map((e, i) => (i === eventIndex ? record : e)))
      }
    )
  }

  async function removeEventRecord(id: string) {
    if (!user || !workspace) return

    queryClient.setQueriesData<EventRecord[]>(
      { queryKey: makeQueryKey([workspace.id]) },
      events => {
        if (!events) return []

        const eventIndex = events.findIndex(e => e.id === id)
        if (eventIndex < 0) return events

        return events.filter((_, i) => i !== eventIndex)
      }
    )
  }

  return { updateEventRecord, removeEventRecord }
}

export function getEventStatus({
  endTime,
  startTime,
}: Pick<IEvent, 'startTime' | 'endTime'>): EventStatus {
  const now = new Date()
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))

  const eventStartTime = new Date(startTime)
  const eventStartDate = new Date(
    Date.UTC(
      eventStartTime.getUTCFullYear(),
      eventStartTime.getUTCMonth(),
      eventStartTime.getUTCDate()
    )
  )

  if (endTime) {
    const eventEndTime = new Date(endTime)
    const eventEndDate = new Date(
      Date.UTC(eventEndTime.getUTCFullYear(), eventEndTime.getUTCMonth(), eventEndTime.getUTCDate())
    )

    if (eventEndDate < today) return 'past'
    else if (eventEndDate >= today) {
      if (eventStartDate > today) return 'upcoming'
      else return 'live'
    }
  } else {
    if (eventStartDate < today) return 'past'
    else if (eventStartDate > today) return 'upcoming'
    else return 'live'
  }
  return 'upcoming'
}

export function getEventsByStatus(events: EventRecord[]) {
  return events.reduce(
    (acc, cur) => {
      acc[cur.status].push(cur)
      return acc
    },
    { live: [], upcoming: [], past: [] } as Record<EventStatus, EventRecord[]>
  )
}

async function getEvents(workspaceIds: string[]) {
  const events = await getDocs({
    collection: 'events',
    fields: partialEventFields,
    sort: { by: 'startTime', order: 'DESC' },
    constraints: [{ field: 'workspaceId', op: 'in', value: workspaceIds }],
  })

  const filledEvents = await Promise.all(events.data.map(makeEventRecord))
  return sortEventRecords(filledEvents)
}

async function makeEventRecord(event: PartialEvent) {
  const partial = pick(event, [...dbProps, ...partialEventFields])
  const { id, workspaceId, hasImage } = partial
  let imageUrl = defaultEventImage
  if (hasImage) {
    const url = await getFileUrl(getEventImagePath(id), workspaceId)
    if (url) imageUrl = url
  }

  return { ...partial, status: getEventStatus(partial), imageUrl } as EventRecord
}

function sortEventRecords(events: EventRecord[]) {
  return [...events].sort((a, b) => {
    if (a.status !== 'upcoming' || b.status !== 'upcoming') return 0
    return a.startTime - b.startTime
  })
}

export const useEvent = () => useContext(EventContext)
export const getEventImagePath = (id: string) => `events/${id}/image`

export function getDaysAway(startTime?: number) {
  if (!startTime) return ''

  const oneDay = 24 * 60 * 60 * 1000
  const today = createTimestamp()

  const diffDays = Math.round(Math.abs((startTime - today) / oneDay))
  if (diffDays <= 365) {
    if (diffDays === 1) return '1 Day Away'
    return `${diffDays.toLocaleString()} Days Away`
  }
  const years = Math.floor(diffDays / 365)
  const days = diffDays % 365
  const yearsLabel = years === 1 ? 'Year' : 'Years'
  if (days === 0) {
    return `${years} ${yearsLabel} Away`
  }
  const daysLabel = days === 1 ? 'Day' : 'Days'
  return `${years} ${yearsLabel} ${days} ${daysLabel} Away`
}

export function isQrCodeConfigurationValid(qrCode?: QRCodeConfiguration, skipIsValid?: boolean) {
  if (!qrCode) return false
  if (!skipIsValid && !qrCode.isValid) return false

  const type = qrCode?.type
  switch (type) {
    case 'demo':
      return true
    case 'converge':
      return Boolean(qrCode.apiKey)
    case 'maritz':
      return Boolean(qrCode.apiKey && qrCode.actCode && qrCode.exhibitorBadgeId)
    case 'xpressleads':
      return Boolean(qrCode.authKey && qrCode.exhibitorId && qrCode.eventCode)
    default:
      return false
  }
}

export function isLockedEvent() {
  const { workspace } = useWorkspace()
  const { event } = useEvent()

  if (!workspace || !event) return false
  const isCurrentEvent = getEventStatus(event) === 'live'

  if (
    workspace.status.value === 'inactive' &&
    (event.startTime > workspace.status.updated || isCurrentEvent)
  )
    return true

  return false
}
